import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "./AuthContext";

export type UserRole = "programme_manager" | "org_unit_lead" | "it_admin" | "governance_lead";

interface UserContextType {
  isAuthenticated: boolean;
  currentRole: UserRole;
  userName: string;
  setIsAuthenticated: (value: boolean) => void;
  setCurrentRole: (role: UserRole) => void;
  setUserName: (name: string) => void;
  logout: () => void;
  getRoleDisplayName: (role: UserRole) => string;
  theme: ThemeSettings;
  setTheme: (theme: ThemeSettings) => void;
  applyTheme: (theme: ThemeSettings) => void;
}

export interface ThemeSettings {
  name: string;
  primary: string;
  accent: string;
  sidebar: string;
}

export const defaultThemes: ThemeSettings[] = [
  { name: "Purple (Default)", primary: "222 47% 14%", accent: "254 51% 35%", sidebar: "254 51% 35%" },
  { name: "Ocean Blue", primary: "217 91% 35%", accent: "199 89% 48%", sidebar: "217 91% 35%" },
  { name: "Forest Green", primary: "152 69% 31%", accent: "152 69% 41%", sidebar: "152 69% 31%" },
  { name: "Sunset Orange", primary: "24 95% 45%", accent: "38 92% 50%", sidebar: "24 95% 45%" },
  { name: "Rose Pink", primary: "340 82% 52%", accent: "340 82% 62%", sidebar: "340 82% 42%" },
  { name: "Slate Gray", primary: "215 20% 35%", accent: "215 20% 50%", sidebar: "215 20% 30%" },
  { name: "Midnight Navy", primary: "230 50% 20%", accent: "230 50% 40%", sidebar: "230 50% 18%" },
  { name: "Teal", primary: "175 70% 35%", accent: "175 70% 45%", sidebar: "175 70% 30%" },
  { name: "Coral", primary: "16 85% 55%", accent: "16 85% 65%", sidebar: "16 85% 45%" },
  { name: "Indigo", primary: "243 75% 50%", accent: "243 75% 60%", sidebar: "243 75% 40%" },
  { name: "Emerald", primary: "160 84% 39%", accent: "160 84% 49%", sidebar: "160 84% 34%" },
  { name: "Amber", primary: "45 93% 47%", accent: "45 93% 57%", sidebar: "45 93% 37%" },
];

const roleDisplayNames: Record<UserRole, string> = {
  programme_manager: "AI Programme Manager",
  org_unit_lead: "Org Unit Lead",
  it_admin: "IT Administrator",
  governance_lead: "Governance Lead",
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin, logout: authLogout } = useAuth();

  // Derive from real auth — no local state for identity
  const isAuthenticated = !!user;
  const currentRole: UserRole = isAdmin ? "programme_manager" : "org_unit_lead";
  const typedUser = user as { first_name?: string; last_name?: string; email?: string } | null;
  const userName = typedUser
    ? ([typedUser.first_name, typedUser.last_name].filter(Boolean).join(" ") || typedUser.email || "User")
    : "Guest";

  // No-ops for the mock identity setter calls in Lovable components
  const setIsAuthenticated = (_value: boolean) => {};
  const setCurrentRole = (_role: UserRole) => {};
  const setUserName = (_name: string) => {};

  const getRoleDisplayName = (role: UserRole) => roleDisplayNames[role];

  const logout = () => {
    authLogout();
  };

  // Theme management (localStorage only, UI feature)
  const [theme, setThemeState] = useState<ThemeSettings>(defaultThemes[0]);

  const applyTheme = (newTheme: ThemeSettings) => {
    const root = document.documentElement;
    root.style.setProperty("--primary", newTheme.primary);
    root.style.setProperty("--accent", newTheme.accent);
    root.style.setProperty("--ring", newTheme.accent);
    root.style.setProperty("--sidebar-background", newTheme.sidebar);
    root.style.setProperty("--sidebar-accent", newTheme.accent);
    root.style.setProperty("--sidebar-border", newTheme.accent);
  };

  const setTheme = (newTheme: ThemeSettings) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem("misia-theme", JSON.stringify(newTheme));
  };

  // Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("misia-theme");
    if (savedTheme) {
      try {
        const parsed = JSON.parse(savedTheme);
        setThemeState(parsed);
        applyTheme(parsed);
      } catch (e) {
        console.error("Failed to parse saved theme");
      }
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        currentRole,
        userName,
        setIsAuthenticated,
        setCurrentRole,
        setUserName,
        logout,
        getRoleDisplayName,
        theme,
        setTheme,
        applyTheme,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
